import { useCallback, useEffect, useState } from 'react';
import { Recipe } from '../../api/Types';
import apiService, { supabase } from '../../api/apiService';
import { resizeImage } from '../../utils/imageUtils';
import { triggerRecipeImageGeneration } from '../../services/recipeImageGeneration';
import { clearRecipeImage, triggerRecipeImageDeletion } from '../../services/recipeImageStorage';
import {
  deleteEditRecipeImage,
  getRecipeImageOrNull,
  resolveEditRecipeImageForSubmit,
  resolveRealtimeEditRecipePreviewImage,
  shouldSkipEditRecipeImageDelete,
  shouldSubscribeToEditRecipeImageRealtime,
  triggerEditRecipeImageGenerationIfNeeded,
} from '../../domains/recipes/use-cases/editRecipeImage';

interface UseEditRecipeImageParams {
  recipe: Recipe;
  isAuthor: boolean;
  token: string | null | undefined;
  onRecipeUpdated: () => void;
  setMessage: (message: string) => void;
  setIsError: (isError: boolean) => void;
  setSubmitting: (submitting: boolean) => void;
}

export interface ResolveImageForSubmitResult {
  imageUrl: string | null;
  shouldAbort: boolean;
}

const useEditRecipeImage = ({
  recipe,
  isAuthor,
  token,
  onRecipeUpdated,
  setMessage,
  setIsError,
  setSubmitting,
}: UseEditRecipeImageParams) => {
  const recipeImage = getRecipeImageOrNull(recipe.image);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(recipeImage);

  useEffect(() => {
    if (shouldSubscribeToEditRecipeImageRealtime(recipe.image, selectedImageFile)) {
      const channel = supabase
        .channel(`recipe_image_update_edit_form:${recipe.id}`)
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'recipes', filter: `id=eq.${recipe.id}` },
          (payload: { new: { id: string; image: string | null } }) => {
            const nextPreviewImage = resolveRealtimeEditRecipePreviewImage(
              payload.new.image,
              previewImage
            );

            if (nextPreviewImage) {
              setPreviewImage(nextPreviewImage);
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [previewImage, recipe.id, recipe.image, selectedImageFile]);

  useEffect(() => {
    setSelectedImageFile(null);
    setPreviewImage(recipeImage);
  }, [recipeImage]);

  const handleImageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      setSelectedImageFile(event.target.files[0]);
      setPreviewImage(URL.createObjectURL(event.target.files[0]));
      return;
    }

    setSelectedImageFile(null);
    setPreviewImage(recipeImage);
  }, [recipeImage]);

  const resolveImageForSubmit = useCallback(async (): Promise<ResolveImageForSubmitResult> => {
    const result = await resolveEditRecipeImageForSubmit({
      currentImageUrl: recipe.image,
      selectedImageFile,
      resizeImage,
      uploadImage: apiService.uploadImage,
      triggerPreviousImageDeletion: triggerRecipeImageDeletion,
    });

    if (result.shouldAbort && result.errorMessage) {
      setMessage(result.errorMessage);
      setIsError(true);
      setSubmitting(false);
    }

    return {
      imageUrl: result.imageUrl,
      shouldAbort: result.shouldAbort,
    };
  }, [recipe.image, selectedImageFile, setIsError, setMessage, setSubmitting]);

  const triggerAiGenerationIfNeeded = useCallback((imageUrl: string | null) => {
    triggerEditRecipeImageGenerationIfNeeded({
      recipeId: recipe.id,
      token,
      imageUrl,
      triggerRecipeImageGeneration,
    });
  }, [recipe.id, token]);

  const handleDeleteImage = useCallback(async () => {
    if (shouldSkipEditRecipeImageDelete({
      isAuthor,
      token,
      previewImage,
      confirmDelete: () => window.confirm("Voulez-vous vraiment supprimer l'image de la recette ?"),
    })) {
      return;
    }

    const currentPreviewImage = previewImage;
    if (!currentPreviewImage) {
      return;
    }

    setSubmitting(true);
    setMessage('');
    setIsError(false);

    const result = await deleteEditRecipeImage({
      recipeId: recipe.id,
      previewImage: currentPreviewImage,
      clearRecipeImage,
    });

    if (result.success) {
      setSelectedImageFile(null);
      setPreviewImage(null);
      setMessage('Image de recette supprimée avec succès !');
      onRecipeUpdated();
    } else if (result.errorMessage) {
      setMessage(result.errorMessage);
      setIsError(true);
    }

    setSubmitting(false);
  }, [isAuthor, onRecipeUpdated, previewImage, recipe.id, setIsError, setMessage, setSubmitting, token]);

  return {
    selectedImageFile,
    setSelectedImageFile,
    previewImage,
    setPreviewImage,
    handleImageChange,
    handleDeleteImage,
    resolveImageForSubmit,
    triggerAiGenerationIfNeeded,
  };
};

export default useEditRecipeImage;
